import { ChangeEvent, HTMLAttributes, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material//Search';
import { Autocomplete, Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Company } from 'api';
import { companySummaryUrl } from 'common/config/urls';
import { PINNED_COMPANIES_KEY, PINNED_FUND_INVESTMENTS_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { useStore } from 'common/store';
import { OutlinedPushPin, PushPin } from 'icons';
import {
  FUND_INVESTMENTS_COMPANY_FILTER_ID,
  FUND_INVESTMENTS_FILTER_ID,
  FUND_INVESTMENTS_FIND_A_COMPANY_PLACEHOLDER,
  FUND_INVESTMENTS_FIND_A_DIRECT_INVESTMENT_PLACEHOLDER,
} from 'pages/FundInvestments/common/constants';
import { getFundInvestmentsSummaryUrl } from 'pages/FundInvestments/common/urls';
import { getObjectValue } from 'utilities';
import { ItemsFilterProps } from './ItemsFilter.props';
import { classes, Root } from './ItemsFilter.styles';
import { ItemOption } from './ItemsFilter.types';

const ItemsFilter = ({ isFundInvestments = false, open, setOpenSidebar, updatePins }: ItemsFilterProps) => {
  const navigate = useNavigate();

  const [storeValue] = useStore();
  const { companyList = [], firmInfo } = getObjectValue(storeValue);

  const { are_fund_investments_enabled: areFundInvestmentsEnabled = false } = getObjectValue(firmInfo);

  const [pinnedItems] = useLocalStorageByUser(
    isFundInvestments ? PINNED_FUND_INVESTMENTS_KEY : PINNED_COMPANIES_KEY,
    []
  );

  const [options, setOptions] = useState<ItemOption[]>([]);

  const itemList = useMemo(
    () =>
      companyList.filter(({ company_type = '' }: Company & { company_type: string }) =>
        isFundInvestments ? company_type === Company.type.INVESTMENT : company_type !== Company.type.INVESTMENT
      ),
    [companyList, isFundInvestments]
  );

  const isPinned = useCallback(
    (option: ItemOption) => pinnedItems.find(o => o.value === option.value) ?? false,
    [pinnedItems]
  );

  useEffect(() => {
    if (Array.isArray(itemList) && itemList.length) {
      const tmpOptions: ItemOption[] = [];

      itemList.forEach(company => {
        tmpOptions.push({
          value: company.company_id,
          label: company.company_name,
        });
      });

      setOptions(tmpOptions);
    }
  }, [itemList]);

  const updateOption = (e: MouseEvent, option: ItemOption) => {
    e.stopPropagation();

    let tmpOptions: ItemOption[] = [];

    if (isPinned(option)) {
      tmpOptions = pinnedItems.filter(o => o.value !== option.value) ?? [];
    } else {
      tmpOptions = [...pinnedItems, option];
    }

    updatePins(tmpOptions);
  };

  const handleChange = (e: ChangeEvent, option: ItemOption | null) => {
    e.stopPropagation();
    if (!option) {
      return;
    }

    const selectedItem = itemList.find(c => c.company_id === option.value);

    if (firmInfo && selectedItem) {
      const { company_type = '' } = getObjectValue(selectedItem);

      const summaryUrl =
        company_type === Company.type.INVESTMENT
          ? getFundInvestmentsSummaryUrl(firmInfo.slug, selectedItem.company_slug)
          : companySummaryUrl(firmInfo.slug, selectedItem.company_slug);
      navigate(summaryUrl);
    }
  };

  const renderInput = (params: any) => (
    <TextField
      {...params}
      fullWidth
      placeholder={
        areFundInvestmentsEnabled
          ? FUND_INVESTMENTS_FIND_A_DIRECT_INVESTMENT_PLACEHOLDER
          : FUND_INVESTMENTS_FIND_A_COMPANY_PLACEHOLDER
      }
      slotProps={{
        input: {
          ...params.InputProps,
          classes: {
            root: classes.input,
            notchedOutline: classes.notchedOutline,
            focused: classes.focused,
          },
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon className={classes.icon} />
            </InputAdornment>
          ),
        },
      }}
      onMouseDownCapture={(e: MouseEvent) => e.stopPropagation()}
      variant="outlined"
    />
  );

  const renderOption = (props: HTMLAttributes<HTMLLIElement>, option: ItemOption) => (
    <li {...props} key={option.value}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            updateOption(e, option);
          }}>
          {isPinned(option) ? <PushPin fontSize="small" /> : <OutlinedPushPin fontSize="small" />}
        </IconButton>
        <Typography>{option.label}</Typography>
      </Box>
    </li>
  );

  return (
    <Root className={clsx(classes.root, { open })}>
      <Autocomplete
        id={isFundInvestments ? FUND_INVESTMENTS_FILTER_ID : FUND_INVESTMENTS_COMPANY_FILTER_ID}
        options={options}
        getOptionLabel={(option: ItemOption) => option.label}
        renderInput={renderInput}
        renderOption={renderOption}
        onChange={handleChange}
        onClose={() => setOpenSidebar(false)}
        clearOnEscape
        autoHighlight
        autoSelect
      />
    </Root>
  );
};

export default ItemsFilter;
